import React, { useCallback, useEffect, useState } from 'react';
import { View } from 'react-native';
import { useFocusEffect } from '@react-navigation/core';

import Layout from '../../../components/Layout';
import {
  AppNavigationProp,
  NavigationParamList,
  ScreenName,
  StackNavigationScreenProps,
} from '../../../navigation/types';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import {
  AdminOrganizationRead,
  AdminCategoryOrderBy,
  AdminTopicRead,
  HelpdeskCategories,
  Order,
  Pagination as IPagination,
  Role,
  CategoriesStatusFilter,
  GetAdminHelpdeskCategoriesParams,
} from '../../../client/interfaces';
import {
  AdminCategoryFilter,
  HelpdeskCategoryTable,
  Pagination,
  ToggleWithText,
} from '../../../components';
import { useMessage } from '../../../contexts/Messages/MessageContext';
import client from '../../../client/client';

import useDebounce from '../../../hooks/useDebounce';
import { useAppTranslation } from '../../../hooks/useAppTranslation';

export const emptyGetAdminHelpdeskCategoriesParams: GetAdminHelpdeskCategoriesParams = {
  orderBy: AdminCategoryOrderBy.createdAtUtc,
  order: Order.DESC,
  page: 1,
  search: '',
  isActive: undefined,
};

const AdminHelpdeskCategoriesScreen: React.FC<
  StackNavigationScreenProps<NavigationParamList, ScreenName.AdminHelpdeskCategoriesScreen>
> = ({ navigation: nav, route }) => {
  const { me, hasRole } = useAuth();
  const navigation = nav as AppNavigationProp;
  const { setMessage } = useMessage();
  const { t } = useAppTranslation();
  const [activeTab, setActiveTab] = useState<HelpdeskCategories>(
    route?.params?.activeTab || HelpdeskCategories.topics,
  );
  const [paginatedOrganizations, setPaginatedOrganizations] = useState<AdminOrganizationRead[]>([]);

  useFocusEffect(
    useCallback(() => {
      if (route?.params?.activeTab) {
        setActiveTab(route.params.activeTab);
      }
    }, [route?.params?.activeTab]),
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [getAdminPaginatedHelpdeskTopicsParams, setGetAdminPaginatedHelpdeskTopicsParams] =
    useState<GetAdminHelpdeskCategoriesParams>({
      ...emptyGetAdminHelpdeskCategoriesParams,
      ...route.params,
    });

  const [paginatedHelpdeskTopics, setPaginatedHelpdeskTopics] = useState<AdminTopicRead[]>([]);
  const [pagination, setPagination] = useState<IPagination>();

  const getAdminPaginatedHelpdeskTopicsParamsJSON = JSON.stringify(
    getAdminPaginatedHelpdeskTopicsParams,
  );

  // Role guard
  useFocusEffect(
    useCallback(() => {
      if (me && !hasRole(Role.publisher)) {
        navigation.navigate(ScreenName.HomeScreen);
      }
    }, [me?.roleList]),
  );

  const fetchData = async () => {
    setIsLoading(true);
    try {
      if (activeTab === HelpdeskCategories.topics) {
        const response = await client.searchAdminHelpdeskTopics(
          getAdminPaginatedHelpdeskTopicsParams,
        );
        setPaginatedHelpdeskTopics(response.items);
        setPagination(response.pagination);
      } else {
        const response = await client.searchAdminHelpdeskOrganizations(
          getAdminPaginatedHelpdeskTopicsParams,
        );
        setPaginatedOrganizations(response.items);
        setPagination(response.pagination);
      }
    } catch (e) {
      console.error(e);
      setMessage({ message: e.message, type: 'error' });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [getAdminPaginatedHelpdeskTopicsParamsJSON, activeTab]);

  useEffect(() => {
    setGetAdminPaginatedHelpdeskTopicsParams((prev) => ({ ...prev, ...route.params }));
  }, [route.params]);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search);

  useEffect(() => {
    setGetAdminPaginatedHelpdeskTopicsParams((prev) => ({ ...prev, search: debouncedSearch }));
  }, [debouncedSearch]);

  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(
    CategoriesStatusFilter.all,
  );

  // Update status param
  useEffect(() => {
    setGetAdminPaginatedHelpdeskTopicsParams((prev) => ({
      ...prev,
      page: 1,
      isActive:
        selectedStatus === CategoriesStatusFilter.all
          ? undefined
          : (selectedStatus as CategoriesStatusFilter),
    }));
  }, [selectedStatus]);

  function emptyFilters() {
    setSearch('');
    setSelectedStatus(CategoriesStatusFilter.all);
    setGetAdminPaginatedHelpdeskTopicsParams((prev) => ({
      ...prev,
      page: 1,
      search: '',
      isActive: undefined,
    }));
  }

  return (
    <Layout title={t('admin:helpdesk_categories:title')} titleMarginBottom={false}>
      <View className="flex-row mt-5">
        <ToggleWithText
          onPress={(key) => {
            setActiveTab(key as HelpdeskCategories);
            emptyFilters();
          }}
          activeOption={activeTab}
          options={[
            { key: HelpdeskCategories.topics, label: t('admin:helpdesk_categories:topics') },
            {
              key: HelpdeskCategories.organizations,
              label: t('admin:helpdesk_categories:organizations'),
            },
          ]}
          isTab={true}
        />
      </View>
      <AdminCategoryFilter
        search={search}
        setSearch={setSearch}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        emptyFilters={emptyFilters}
        activeTab={activeTab}
        pagination={pagination}
        navigation={navigation}
      />

      <HelpdeskCategoryTable
        rows={
          activeTab === HelpdeskCategories.topics ? paginatedHelpdeskTopics : paginatedOrganizations
        }
        paramSetter={setGetAdminPaginatedHelpdeskTopicsParams}
        params={getAdminPaginatedHelpdeskTopicsParams}
        refresh={fetchData}
        isLoading={isLoading}
        setPaginated={
          activeTab === HelpdeskCategories.topics
            ? setPaginatedHelpdeskTopics
            : setPaginatedOrganizations
        }
        activeTab={activeTab}
      />
      {pagination && (
        <Pagination
          data={pagination}
          paramSetter={setGetAdminPaginatedHelpdeskTopicsParams}
          params={getAdminPaginatedHelpdeskTopicsParams}
        />
      )}
    </Layout>
  );
};

export default AdminHelpdeskCategoriesScreen;
